import { useEffect, useState } from "react";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import { api, inr } from "../../lib/api";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { CreditCard, ShieldCheck, ChevronRight, Truck, Lock, Tag, Banknote, Check, Building2 } from "lucide-react";
import { logoUrlFor } from "../../lib/logo";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: user?.email || "", phone: "",
    line1: "", city: "", state: "", pincode: "", country: "India",
    buyer_gstin: "", buyer_company_name: "",
  });
  const [showGstin, setShowGstin] = useState(false);

  // Prefill GSTIN + company from the user's last B2B checkout.
  useEffect(() => {
    if (!user) return;
    setForm(f => ({
      ...f,
      email: f.email || user.email || "",
      buyer_gstin: f.buyer_gstin || user.last_gstin || "",
      buyer_company_name: f.buyer_company_name || user.last_gstin_company || "",
    }));
    if (user.last_gstin) setShowGstin(true);
  }, [user]);
  const [coupon, setCoupon] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponPreview, setCouponPreview] = useState(null); // { discount, free_shipping, title }
  const [placing, setPlacing] = useState(false);
  const [config, setConfig] = useState({ razorpay_enabled: false, razorpay_key_id: null, cod_fee: 9 });
  const [method, setMethod] = useState("cod"); // "cod" | "razorpay"

  useEffect(() => {
    api.get("/payments/config").then(r => {
      setConfig(r.data);
      if (r.data?.razorpay_enabled) setMethod("razorpay");
    }).catch(()=>{});
  }, []);

  const codeUpper = (coupon || "").trim().toUpperCase();
  const previewDiscount = couponApplied && couponPreview ? Number(couponPreview.discount || 0) : 0;
  const previewFreeShip = couponApplied && couponPreview ? !!couponPreview.free_shipping : false;
  const discount = previewDiscount;
  const shipping = (previewFreeShip || subtotal >= 500) ? 0 : 49;
  const codFee = method === "cod" ? Number(config.cod_fee ?? 9) : 0;
  // GST is embedded in the product price on the storefront — the cart no
  // longer adds a separate 5% tax line. Invoice PDFs still show the
  // GST breakdown for B2B input-tax-credit claims.
  const tax = 0;
  const total = Math.round(subtotal - discount + shipping + codFee);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const applyCoupon = async () => {
    const code = codeUpper;
    if (!code) { setCouponApplied(false); setCouponPreview(null); return; }
    try {
      const r = await api.post("/coupons/validate", {
        code,
        items: items.map(i => ({ product_id: i.product_id, variant_sku: i.variant_sku,
                                  variant_label: i.variant_label, qty: i.qty })),
        email: form.email || undefined,
      });
      if (r.data?.valid) {
        setCouponApplied(true);
        setCouponPreview({
          discount: r.data.discount,
          free_shipping: r.data.free_shipping,
          title: r.data.title,
          discount_type: r.data.discount_type,
        });
        toast.success(`${r.data.title || code} applied`);
      } else {
        setCouponApplied(false);
        setCouponPreview(null);
        toast.error(r.data?.error || "Invalid coupon");
      }
    } catch (e) {
      setCouponApplied(false);
      setCouponPreview(null);
      toast.error(e.response?.data?.detail || "Failed to validate coupon");
    }
  };

  const payWithRazorpay = async () => {
    if (typeof window.Razorpay === "undefined") {
      toast.error("Razorpay is still loading, please retry in a moment");
      return;
    }
    const cartPayload = {
      items: items.map(i => ({ product_id: i.product_id, variant_sku: i.variant_sku,
                                variant_label: i.variant_label, qty: i.qty })),
      address: { line1: form.line1, city: form.city, state: form.state, pincode: form.pincode, country: form.country },
      email: form.email, phone: form.phone,
      payment_method: "razorpay", coupon_code: (coupon || "").trim() || null,
      buyer_gstin: (form.buyer_gstin || "").trim().toUpperCase() || null,
      buyer_company_name: (form.buyer_company_name || "").trim() || null,
    };
    let cr;
    try {
      cr = await api.post("/payments/razorpay/create-order", cartPayload);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to initiate payment");
      setPlacing(false); return;
    }
    const options = {
      key: cr.data.key_id,
      amount: cr.data.amount, currency: cr.data.currency,
      order_id: cr.data.razorpay_order_id,
      name: "DailyBeans",
      description: `${items.length} item${items.length!==1?"s":""} · ${inr(cr.data.total)}`,
      image: `${process.env.REACT_APP_BACKEND_URL || ""}${logoUrlFor()}`,
      prefill: { email: form.email, contact: form.phone, name: user?.name || "" },
      theme: { color: "#3B2F2F" },
      handler: async (res) => {
        try {
          const vr = await api.post("/payments/razorpay/verify", {
            checkout_id: cr.data.checkout_id,
            razorpay_order_id: res.razorpay_order_id,
            razorpay_payment_id: res.razorpay_payment_id,
            razorpay_signature: res.razorpay_signature,
          });
          clear();
          navigate(`/order/${vr.data.order_id}`);
        } catch (err) {
          toast.error(err.response?.data?.detail || "Payment verification failed");
          setPlacing(false);
        }
      },
      modal: { ondismiss: () => { setPlacing(false); toast.message("Payment cancelled"); } },
    };
    const rzp = new window.Razorpay(options);
    rzp.on("payment.failed", (resp) => {
      toast.error(`Payment failed: ${resp.error?.description || "Unknown error"}`);
      setPlacing(false);
    });
    rzp.open();
  };

  const placeCod = async () => {
    try {
      const r = await api.post("/orders", {
        items: items.map(i => ({ product_id: i.product_id, variant_sku: i.variant_sku,
                                  variant_label: i.variant_label, qty: i.qty })),
        address: { line1: form.line1, city: form.city, state: form.state, pincode: form.pincode, country: form.country },
        email: form.email, phone: form.phone,
        payment_method: "cod", coupon_code: (coupon || "").trim() || null,
        buyer_gstin: (form.buyer_gstin || "").trim().toUpperCase() || null,
        buyer_company_name: (form.buyer_company_name || "").trim() || null,
      });
      clear();
      toast.success("Order placed! Pay in cash when it arrives.");
      navigate(`/order/${r.data.order_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to place order");
      setPlacing(false);
    }
  };

  const place = async (e) => {
    e.preventDefault();
    if (items.length === 0) return toast.error("Your bag is empty");
    setPlacing(true);
    if (method === "razorpay" && config.razorpay_enabled) await payWithRazorpay();
    else await placeCod();
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-24 text-center relative">
        <div className="blob blob-gold" style={{ width: 400, height: 400, top: 0, left: "50%", transform: "translateX(-50%)" }}/>
        <div className="relative">
          <div className="w-20 h-20 rounded-full bg-[#FEF3E2] flex items-center justify-center mx-auto mb-4">
            <Tag className="w-10 h-10 text-[#8B7355]"/>
          </div>
          <h1 className="font-serif-display text-5xl">Your bag is empty</h1>
          <p className="text-stone-500 mt-3">Add some coffee to get started.</p>
          <Link to="/shop" className="inline-flex items-center gap-2 mt-6 bg-[#3B2F2F] text-white rounded-full px-6 py-3 hover:bg-black">
            Browse the shop <ChevronRight className="w-4 h-4"/>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="blob blob-gold" style={{ width: 500, height: 500, top: 100, right: -180 }} />
      <div className="blob blob-sage" style={{ width: 400, height: 400, bottom: 200, left: -150 }} />

      <div className="max-w-6xl mx-auto px-4 md:px-8 pt-8 pb-16 relative z-10">
        <nav className="flex items-center gap-2 text-xs text-stone-500 mb-6" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-[#3B2F2F]">Home</Link>
          <ChevronRight className="w-3 h-3"/>
          <Link to="/shop" className="hover:text-[#3B2F2F]">Shop</Link>
          <ChevronRight className="w-3 h-3"/>
          <span className="text-stone-800">Checkout</span>
        </nav>

        <div className="mb-8">
          <div className="uppercase text-xs tracking-[0.3em] text-[#8B7355] mb-2">Almost there</div>
          <h1 className="font-serif-display text-5xl md:text-6xl">Checkout</h1>
          <div className="flex items-center gap-3 mt-3 text-xs text-stone-500">
            <span className="flex items-center gap-1"><Lock className="w-3 h-3"/> Secure checkout</span>
            <span>·</span>
            <span className="flex items-center gap-1"><Truck className="w-3 h-3"/> 2-4 day delivery</span>
          </div>
        </div>

        <div className="grid md:grid-cols-5 gap-8">
          <form onSubmit={place} className="md:col-span-3 space-y-6" data-testid="checkout-form">
            <section className="bg-white/80 backdrop-blur border border-stone-200 rounded-3xl p-6 md:p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-8 rounded-full bg-[#3B2F2F] text-white flex items-center justify-center font-outfit font-bold text-sm">1</div>
                <h2 className="font-serif-display text-3xl">Contact</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <FieldInput required type="email" label="Email" value={form.email} onChange={v=>upd("email",v)} testid="chk-email"/>
                <FieldInput required label="Phone" value={form.phone} onChange={v=>upd("phone",v)} testid="chk-phone"/>
              </div>
            </section>

            <section className="bg-white/80 backdrop-blur border border-stone-200 rounded-3xl p-6 md:p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-8 rounded-full bg-[#3B2F2F] text-white flex items-center justify-center font-outfit font-bold text-sm">2</div>
                <h2 className="font-serif-display text-3xl">Delivery address</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2"><FieldInput required label="Street address" value={form.line1} onChange={v=>upd("line1",v)} testid="chk-line1"/></div>
                <FieldInput required label="City" value={form.city} onChange={v=>upd("city",v)} testid="chk-city"/>
                <FieldInput required label="State" value={form.state} onChange={v=>upd("state",v)} testid="chk-state"/>
                <FieldInput required label="Pincode" value={form.pincode} onChange={v=>upd("pincode",v)} testid="chk-pincode"/>
                <FieldInput label="Country" value={form.country} onChange={v=>upd("country",v)} testid="chk-country"/>
              </div>
            </section>

            <section className="bg-white/80 backdrop-blur border border-stone-200 rounded-3xl p-6 md:p-8 shadow-sm" data-testid="chk-gstin-section">
              {!showGstin ? (
                <button type="button" onClick={()=>setShowGstin(true)} data-testid="chk-gstin-toggle"
                        className="flex items-center gap-3 text-sm text-[#3B2F2F] hover:text-black">
                  <Building2 className="w-5 h-5 text-[#D4AF37]"/>
                  <span className="font-outfit font-semibold">Buying for a business?</span>
                  <span className="text-stone-500">Add GSTIN to claim input tax credit</span>
                </button>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <Building2 className="w-6 h-6 text-[#D4AF37]"/>
                    <h2 className="font-serif-display text-2xl">Business GST details</h2>
                    <button type="button" onClick={()=>{setShowGstin(false); upd("buyer_gstin",""); upd("buyer_company_name","");}} className="ml-auto text-xs text-stone-500 hover:text-stone-800">Skip</button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <FieldInput label="Company name" value={form.buyer_company_name} onChange={v=>upd("buyer_company_name",v)} testid="chk-buyer-company"/>
                    <FieldInput label="GSTIN (15 chars)" value={form.buyer_gstin} onChange={v=>upd("buyer_gstin",(v||"").toUpperCase())} testid="chk-buyer-gstin"/>
                  </div>
                  <div className="text-[11px] text-stone-500 mt-2">Your GSTIN will be printed on the tax invoice PDF so your business can claim input tax credit.</div>
                </>
              )}
            </section>

            <section className="bg-white/80 backdrop-blur border border-stone-200 rounded-3xl p-6 md:p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-8 rounded-full bg-[#3B2F2F] text-white flex items-center justify-center font-outfit font-bold text-sm">3</div>
                <h2 className="font-serif-display text-3xl">Payment</h2>
              </div>

              <div className="space-y-3">
                <PayOption
                  selected={method === "cod"} onSelect={() => setMethod("cod")}
                  testid="pay-cod"
                  icon={<Banknote className="w-6 h-6 text-[#2E4A35]"/>}
                  title="Cash on Delivery"
                  subtitle={`Pay in cash when your parcel arrives. A small ₹${Number(config.cod_fee ?? 9)} handling fee applies.`}
                  badge="No card needed"
                  iconBg="#E8EDDF"
                />
                {config.razorpay_enabled ? (
                  <PayOption
                    selected={method === "razorpay"} onSelect={() => setMethod("razorpay")}
                    testid="pay-razorpay"
                    icon={<CreditCard className="w-6 h-6 text-[#D4AF37]"/>}
                    title="Pay online with Razorpay"
                    subtitle="UPI · Cards · Netbanking · Wallets · EMI. All in INR, signature-verified server-side."
                    badge="Instant"
                    iconBg="#FEF3E2"
                  />
                ) : (
                  <div className="border border-dashed border-stone-300 rounded-2xl p-4 bg-stone-50 text-xs text-stone-500 flex items-center gap-2">
                    <CreditCard className="w-4 h-4 shrink-0"/>
                    Online payments (Razorpay) — coming soon. Until then, please use Cash on Delivery.
                  </div>
                )}
              </div>
            </section>

            <button disabled={placing} type="submit" data-testid="place-order-btn"
                    className="w-full bg-[#3B2F2F] text-white rounded-full py-4 text-lg font-medium hover:bg-black disabled:bg-stone-400 shadow-2xl shadow-[#3B2F2F]/20 hover:shadow-xl transition-shadow">
              {placing ? "Processing…"
                : method === "razorpay" ? `Pay ${inr(total)} with Razorpay`
                : `Place COD order · ${inr(total)}`}
            </button>
            <div className="text-center text-xs text-stone-500 flex items-center justify-center gap-4">
              <span className="flex items-center gap-1"><Lock className="w-3 h-3"/> 256-bit SSL</span>
              <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3"/> No card data stored</span>
            </div>
          </form>

          <aside className="md:col-span-2 h-fit md:sticky md:top-24 space-y-4">
            <div className="bg-white border border-stone-200 rounded-3xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-serif-display text-2xl">Order summary</h3>
                <span className="text-xs text-stone-500 bg-[#F5F0E6] px-2 py-1 rounded-full">{items.length} item{items.length!==1?"s":""}</span>
              </div>
              <div className="space-y-4 max-h-64 overflow-auto mb-5 pr-1">
                {items.map(i => (
                  <div key={i.variant_sku} className="flex gap-3 text-sm">
                    <div className="relative shrink-0">
                      <img src={i.image} className="w-16 h-16 rounded-xl object-cover border border-stone-100" alt=""/>
                      <span className="absolute -top-1.5 -right-1.5 bg-[#3B2F2F] text-white text-[10px] font-outfit font-bold w-5 h-5 rounded-full flex items-center justify-center">{i.qty}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="truncate font-medium">{i.product_name}</div>
                      <div className="text-xs text-stone-500">{i.variant_label}</div>
                    </div>
                    <div className="font-outfit font-semibold text-[#3B2F2F]">{inr(i.unit_price*i.qty)}</div>
                  </div>
                ))}
              </div>

              <div className="border-t border-dashed border-stone-200 pt-4">
                <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2 flex items-center gap-1"><Tag className="w-3 h-3"/> Have a coupon?</div>
                <div className="flex gap-2">
                  <Input value={coupon} onChange={e=>{setCoupon(e.target.value); setCouponApplied(false); setCouponPreview(null);}} placeholder="Try WELCOME10" data-testid="coupon-input" className="uppercase"/>
                  <button type="button" onClick={applyCoupon} data-testid="apply-coupon"
                          className="bg-[#3B2F2F] text-white rounded-md px-4 text-sm font-medium hover:bg-black">Apply</button>
                </div>
                {couponApplied && <div className="text-xs text-[#2E4A35] mt-2 flex items-center gap-1" data-testid="coupon-applied"><ShieldCheck className="w-3 h-3"/> {couponPreview?.title || "Coupon"} — {previewFreeShip ? "free shipping" : `saved ${inr(discount)}`}!</div>}
              </div>

              <div className="mt-5 space-y-2 text-sm border-t border-stone-200 pt-4">
                <Row l="Subtotal" v={inr(subtotal)}/>
                {discount > 0 && <Row l="Discount" v={`- ${inr(discount)}`} accent/>}
                <Row l="Shipping" v={shipping===0?<span className="text-[#2E4A35] font-semibold">FREE</span>:inr(shipping)}/>
                {codFee > 0 && <Row l="COD handling" v={inr(codFee)}/>}
                <div className="text-[11px] text-stone-400 pt-1">All prices inclusive of taxes</div>
                <div className="flex justify-between font-outfit font-bold text-xl pt-3 border-t border-stone-200">
                  <span>Total</span>
                  <span data-testid="order-total" className="text-[#3B2F2F]">{inr(total)}</span>
                </div>
              </div>

              {shipping > 0 && subtotal < 500 && (
                <div className="mt-4 bg-[#FEF3E2] border border-[#D4AF37]/40 rounded-lg p-3 text-xs text-stone-700">
                  Add <span className="font-outfit font-bold text-[#3B2F2F]">{inr(500 - subtotal)}</span> more for <span className="font-semibold">FREE shipping</span>
                  <div className="mt-2 h-1.5 bg-white rounded-full overflow-hidden">
                    <div className="h-full bg-[#D4AF37] transition-all" style={{width: `${Math.min(100, (subtotal/500)*100)}%`}}/>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-gradient-to-br from-[#3B2F2F] to-[#2A2020] text-[#FDFBF7] rounded-2xl p-5 text-sm">
              <div className="flex items-start gap-3">
                <Truck className="w-5 h-5 text-[#D4AF37] shrink-0 mt-0.5"/>
                <div>
                  <div className="font-outfit font-semibold">Delivered in 2-4 days</div>
                  <div className="text-xs text-[#FDFBF7]/70 mt-1">Tracked shipping across India. Order before 4 PM for same-day dispatch.</div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function PayOption({ selected, onSelect, testid, icon, title, subtitle, badge, iconBg }) {
  return (
    <button type="button" onClick={onSelect} data-testid={testid}
            className={`w-full text-left rounded-2xl p-5 flex items-start gap-4 transition-all ${
              selected
                ? "border-2 border-[#3B2F2F] bg-[#FEF3E2]/40 shadow-md"
                : "border border-stone-200 bg-white hover:border-stone-300"
            }`}>
      <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{background: iconBg}}>
        {icon}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <div className="font-outfit font-bold text-lg text-[#3B2F2F]">{title}</div>
          {badge && <span className="text-[10px] uppercase tracking-widest font-semibold text-[#8B7355] bg-white border border-stone-200 rounded-full px-2 py-0.5">{badge}</span>}
        </div>
        <div className="text-sm text-stone-600 mt-1">{subtitle}</div>
      </div>
      <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 border-2 ${selected ? "bg-[#3B2F2F] border-[#3B2F2F]" : "border-stone-300"}`}>
        {selected && <Check className="w-4 h-4 text-white"/>}
      </div>
    </button>
  );
}

function FieldInput({ label, value, onChange, testid, ...props }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-stone-500 font-outfit font-semibold mb-1.5">{label}</div>
      <Input value={value} onChange={e=>onChange(e.target.value)} data-testid={testid} className="bg-white border-stone-200 focus-visible:ring-[#D4AF37]" {...props}/>
    </label>
  );
}

const Row = ({ l, v, accent }) => (
  <div className="flex justify-between"><span className="text-stone-500">{l}</span><span className={accent?"text-[#2E4A35] font-semibold":""}>{v}</span></div>
);
